import React, { useEffect, useMemo, useState } from "react";
import { fetchBoqSnapshots } from "../api/productTablesApi";

const fmt = (n) =>
  Number(n || 0).toLocaleString("ka-GE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (n) => Number(n || 0).toLocaleString("ka-GE", { maximumFractionDigits: 0 });

const COLORS = { mat: "#14b8c4", lab: "#22c55e", eq: "#f59e0b" };

const card = {
  background: "var(--surface-soft)", border: "1px solid var(--surface-border)",
  borderRadius: 12, padding: "12px 14px",
};

function Kpi({ label, value, sub, subColor }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, marginTop: 4, color: subColor || "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

function Trend({ points }) {
  if (points.length < 2) return null;
  const W = 300, H = 70, P = 6;
  const vals = points.map((p) => p.total);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const xy = points.map((p, i) => [
    P + (i * (W - 2 * P)) / (points.length - 1),
    H - P - ((p.total - min) / span) * (H - 2 * P),
  ]);
  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${P},${H - P} ${line} ${W - P},${H - P}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Cost trend">
      <polygon points={area} fill="rgba(20,184,196,0.15)" />
      <polyline points={line} fill="none" stroke="#14b8c4" strokeWidth="2" strokeLinejoin="round" />
      {xy.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === xy.length - 1 ? 4 : 2.5} fill="#14b8c4" />
      ))}
    </svg>
  );
}

function WhatIfSlider({ label, color, value, onChange }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <span style={{ color }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{value > 0 ? "+" : ""}{value}%</span>
      </div>
      <input
        type="range" min={-30} max={30} step={1} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: color }}
      />
    </div>
  );
}

export default function ProjectDashboard({ project, token, projects = [], onGoToBOQ, onSaveBudget }) {
  const [snapshots, setSnapshots] = useState(null);
  const [error, setError] = useState("");
  const [peers, setPeers] = useState(null); // cost per m² of similar projects
  const [whatIf, setWhatIf] = useState({ mat: 0, lab: 0, eq: 0 });
  const [budgetInput, setBudgetInput] = useState("");
  const [budgetBusy, setBudgetBusy] = useState(false);
  const budget = Number(project?.cost_config?.budget) || 0;

  useEffect(() => {
    setBudgetInput(budget ? String(budget) : "");
  }, [budget, project?.id]);

  // Benchmark: latest cost per m² of the user's other projects of the same building type.
  useEffect(() => {
    let cancelled = false;
    setPeers(null);
    const type = String(project?.building_type || "").trim().toLowerCase();
    if (!type || !token) return undefined;
    const similar = projects
      .filter((p) => p.id !== project?.id && String(p.building_type || "").trim().toLowerCase() === type && Number(p.area) > 0)
      .slice(0, 8);
    if (!similar.length) { setPeers([]); return undefined; }
    Promise.all(
      similar.map((p) =>
        fetchBoqSnapshots(token, p.id, 1, 0)
          .then((r) => {
            const s = (r?.items || [])[0];
            return s && s.total_cost > 0 ? { name: p.name, perM2: s.total_cost / Number(p.area) } : null;
          })
          .catch(() => null)
      )
    ).then((list) => { if (!cancelled) setPeers(list.filter(Boolean)); });
    return () => { cancelled = true; };
  }, [project?.id, project?.building_type, token, projects]);

  useEffect(() => {
    let cancelled = false;
    setSnapshots(null);
    setError("");
    if (!project?.id || !token) return undefined;
    fetchBoqSnapshots(token, project.id, 50, 0)
      .then((res) => { if (!cancelled) setSnapshots(res?.items || []); })
      .catch((e) => { if (!cancelled) setError(e?.message || "ჩატვირთვა ვერ მოხერხდა"); });
    return () => { cancelled = true; };
  }, [project?.id, token]);

  const view = useMemo(() => {
    if (!snapshots || !snapshots.length) return null;
    const latest = snapshots[0];
    // Trend and "change" only compare saves of the same BOQ version.
    const sameVersion = snapshots.filter((s) => (s.version_id || "") === (latest.version_id || ""));
    const previous = sameVersion[1] || null;
    const trend = [...sameVersion].reverse().map((s) => ({ total: s.total_cost, at: s.created_at }));
    const area = Number(project?.area) || 0;
    const direct = latest.total_material + latest.total_labour + latest.total_equipment || latest.total_cost;
    const parts = [
      { key: "mat", label: "მასალა", value: latest.total_material },
      { key: "lab", label: "შრომა", value: latest.total_labour },
      { key: "eq", label: "ტექნიკა", value: latest.total_equipment },
    ];
    // Top-level chapters only ("2", not "2.1"); a parent already includes its children.
    const chapters = [...(latest.chapters || [])]
      .filter((c) => c.total > 0 && !String(c.display_id || "").replace(/\.0+$/, "").includes("."))
      .sort((a, b) => b.total - a.total);
    const delta = previous ? latest.total_cost - previous.total_cost : null;
    return { latest, previous, trend, area, direct, parts, chapters, delta, count: sameVersion.length };
  }, [snapshots, project]);

  return (
    <div data-no-translate="true" style={{ padding: "4px 0 24px" }}>
      <h3 style={{ margin: "14px 0 4px" }}>დაფა</h3>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
        ბოლო შენახული ხარჯთაღრიცხვის მიმოხილვა
      </div>

      {error && <div className="msg-error">{error}</div>}
      {!error && snapshots === null && (
        <div style={{ ...card, textAlign: "center", color: "var(--text-muted)" }}>იტვირთება...</div>
      )}

      {snapshots && !view && (
        <div style={{ ...card, textAlign: "center" }}>
          <div style={{ fontSize: 30, marginBottom: 6 }}>📊</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>მონაცემები ჯერ არ არის</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
            დაფა შეივსება პირველი შენახვის (Save) შემდეგ.
          </div>
          {onGoToBOQ && <button className="primary" onClick={onGoToBOQ} style={{ padding: "8px 14px" }}>BOQ-ზე გადასვლა</button>}
        </div>
      )}

      {view && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <Kpi label="მთლიანი ღირებულება" value={`${fmt0(view.latest.total_cost)} ₾`} />
            <Kpi
              label="1 მ² ღირებულება"
              value={view.area > 0 ? `${fmt0(view.latest.total_cost / view.area)} ₾` : "—"}
              sub={view.area > 0 ? `${fmt0(view.area)} მ²` : "ფართობი მითითებული არ არის"}
            />
            <Kpi
              label="ცვლილება წინა შენახვასთან"
              value={view.delta === null ? "—" : `${view.delta > 0 ? "+" : ""}${fmt0(view.delta)} ₾`}
              subColor={view.delta > 0 ? "#fb7185" : view.delta < 0 ? "#34d399" : undefined}
              sub={view.delta === null ? "ერთადერთი შენახვა" : view.delta > 0 ? "გაიზარდა" : view.delta < 0 ? "შემცირდა" : "უცვლელია"}
            />
            <Kpi
              label="ბოლო შენახვა"
              value={view.latest.created_at ? new Date(view.latest.created_at).toLocaleDateString("ka-GE") : "—"}
              sub={`${view.latest.created_by_name || "—"} · ${view.count} შენახვა`}
            />
          </div>

          {/* Budget target */}
          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>ბიუჯეტის მიზანი</div>
            {budget > 0 ? (() => {
              const used = view.latest.total_cost / budget;
              const color = used > 1 ? "#fb7185" : used > 0.9 ? "#f59e0b" : "#34d399";
              return (
                <>
                  <div style={{ height: 12, borderRadius: 8, background: "var(--surface-border)", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(used * 100, 100)}%`, height: "100%", background: color, transition: "width 0.6s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 6 }}>
                    <span style={{ color, fontWeight: 600 }}>{(used * 100).toFixed(1)}%</span>
                    <span>
                      {used > 1
                        ? `ბიუჯეტზე მეტი: ${fmt0(view.latest.total_cost - budget)} ₾`
                        : `დარჩენილია: ${fmt0(budget - view.latest.total_cost)} ₾`}
                    </span>
                  </div>
                </>
              );
            })() : (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
                მიუთითე სამიზნე ბიუჯეტი და დაფა აჩვენებს, რამდენი დარჩა.
              </div>
            )}
            {onSaveBudget && (
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <input
                  type="number" min={0} placeholder="ბიუჯეტი ₾" value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)" }}
                />
                <button
                  className="primary" disabled={budgetBusy}
                  onClick={async () => {
                    setBudgetBusy(true);
                    try { await onSaveBudget(Number(budgetInput) || 0); } finally { setBudgetBusy(false); }
                  }}
                  style={{ padding: "6px 14px" }}
                >
                  {budgetBusy ? "..." : "შენახვა"}
                </button>
              </div>
            )}
          </div>

          {/* Benchmark vs similar projects */}
          {view.area > 0 && peers && peers.length > 0 && (() => {
            const avg = peers.reduce((s, p) => s + p.perM2, 0) / peers.length;
            const mine = view.latest.total_cost / view.area;
            const diff = ((mine - avg) / avg) * 100;
            return (
              <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>შედარება მსგავს პროექტებთან</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: diff > 0 ? "#fb7185" : "#34d399" }}>
                  {diff > 0 ? "+" : ""}{diff.toFixed(1)}%
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                  შენი 1 მ²: {fmt0(mine)} ₾ · საშუალო ({peers.length} პროექტი, ტიპი „{project.building_type}“): {fmt0(avg)} ₾
                </div>
              </div>
            );
          })()}

          {/* What-if simulator */}
          {(() => {
            const factor = view.direct ? view.latest.total_cost / view.direct : 1; // overhead + VAT multiplier
            const newDirect =
              view.latest.total_material * (1 + whatIf.mat / 100) +
              view.latest.total_labour * (1 + whatIf.lab / 100) +
              view.latest.total_equipment * (1 + whatIf.eq / 100);
            const newTotal = newDirect * factor;
            const delta = newTotal - view.latest.total_cost;
            const changed = whatIf.mat || whatIf.lab || whatIf.eq;
            return (
              <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>რა მოხდება, თუ ფასები შეიცვლება?</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                  გადაადგილე ღილაკები და ნახე ახალი ჯამი (დანამატებით)
                </div>
                <WhatIfSlider label="მასალა" color={COLORS.mat} value={whatIf.mat} onChange={(v) => setWhatIf((w) => ({ ...w, mat: v }))} />
                <WhatIfSlider label="შრომა" color={COLORS.lab} value={whatIf.lab} onChange={(v) => setWhatIf((w) => ({ ...w, lab: v }))} />
                <WhatIfSlider label="ტექნიკა" color={COLORS.eq} value={whatIf.eq} onChange={(v) => setWhatIf((w) => ({ ...w, eq: v }))} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--surface-border)" }}>
                  <span style={{ fontSize: 20, fontWeight: 700 }}>{fmt0(newTotal)} ₾</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: !changed ? "var(--text-muted)" : delta > 0 ? "#fb7185" : "#34d399" }}>
                    {changed ? `${delta > 0 ? "+" : ""}${fmt0(delta)} ₾` : "ცვლილების გარეშე"}
                  </span>
                </div>
                {changed ? (
                  <button onClick={() => setWhatIf({ mat: 0, lab: 0, eq: 0 })} style={{ marginTop: 8, padding: "4px 10px", fontSize: 11, background: "transparent", border: "1px solid var(--surface-border)", borderRadius: 6, color: "var(--text-muted)", cursor: "pointer" }}>
                    გასუფთავება
                  </button>
                ) : null}
              </div>
            );
          })()}

          {/* Cost split */}
          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>ხარჯების სტრუქტურა</div>
            <div style={{ display: "flex", height: 14, borderRadius: 8, overflow: "hidden", background: "var(--surface-border)" }}>
              {view.parts.map((p) => (
                <div
                  key={p.key}
                  title={`${p.label}: ${fmt(p.value)} ₾`}
                  style={{ width: `${view.direct ? (p.value / view.direct) * 100 : 0}%`, background: COLORS[p.key], transition: "width 0.5s ease" }}
                />
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10 }}>
              {view.parts.map((p) => (
                <div key={p.key}>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[p.key], display: "inline-block" }} />
                    {p.label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt0(p.value)} ₾</div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                    {view.direct ? `${((p.value / view.direct) * 100).toFixed(1)}%` : "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top chapters */}
          {view.chapters.length > 0 && (
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>ყველაზე ძვირი თავები</div>
              {view.chapters.slice(0, 6).map((c, i) => {
                const share = view.latest.total_cost ? (c.total / view.latest.total_cost) * 100 : 0;
                return (
                  <div key={`${c.display_id}-${i}`} style={{ marginBottom: 9 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, gap: 8 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.display_id ? `${c.display_id}. ` : ""}{c.name}
                      </span>
                      <span style={{ flexShrink: 0, fontWeight: 600 }}>{fmt0(c.total)} ₾</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 4, background: "var(--surface-border)", marginTop: 4, overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(share, 100)}%`, height: "100%", background: "linear-gradient(90deg,#14b8c4,#2563eb)", transition: "width 0.5s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Trend */}
          {view.trend.length > 1 && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>ღირებულების დინამიკა</div>
              <Trend points={view.trend} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--text-muted)" }}>
                <span>{new Date(view.trend[0].at).toLocaleDateString("ka-GE")}</span>
                <span>{new Date(view.trend[view.trend.length - 1].at).toLocaleDateString("ka-GE")}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
